import React, { ReactElement } from 'react';

import { useTranslation } from 'react-i18next';
import { PickTour, PaginationData, OrderType } from '../../../../../types';

import ReturnToQueueIcon from '../../../../../assets/icons/ReturnToQueueIcon';
import Button from '../../../../buttons/Button';

import { LANGUAGE_SPANISH, ORDER_TYPE_OVERSTOCK, ORDER_TYPE_RECALL } from '../../../../../constants';

import {
  mergeClassNames,
  calculateSkippedQuantity,
  calculateTotalQuantity,
  calculateAmountToPick,
} from '../../../../../utils';

import styles from './PickTourExitReview.css';

type Props = {
  pickTour: PickTour;
  paginationData: PaginationData;
  returnToOrderList: () => void;
  currentLanguage: string;
  orderType?: OrderType;
};

const PickTourExitReview = ({
  pickTour,
  paginationData,
  returnToOrderList,
  currentLanguage,
  orderType,
}: Props): ReactElement => {
  const { t } = useTranslation();
  const translationRoot = 'PICK_TOUR_COMPLETE_OR_EXIT';

  // Only count items that are eligible for picking (exclude BHPUT, HUB, VDP, OSB)
  const eligiblePicks = pickTour.picks.filter(pick => pick.isEligibleForPicking);

  // Calculate picked quantity based on order type
  const currentPagePickedQuantity = orderType !== ORDER_TYPE_RECALL
    ? eligiblePicks.reduce((total, pick) => {
        // For non-recall: count fully picked or skipped items
        const isFullyPickedOrSkipped = pick.pickedQuantity === calculateAmountToPick(pick) || pick.isSkipped;
        return total + (isFullyPickedOrSkipped ? pick.pickedQuantity : 0);
      }, 0)
    : eligiblePicks.reduce((total, pick) => {
        // For recall: count only fully picked items
        const isFullyPicked = pick.pickedQuantity >= calculateAmountToPick(pick);
        return total + (isFullyPicked ? pick.pickedQuantity : 0);
      }, 0);

  // Add picked items from previous pages if paginated
  const previousPagesPickedQuantity = paginationData.isPaginated
    ? paginationData.totalItemsPicked - paginationData.initialPageItemsPicked
    : 0;

  const resolvedPickPickedQuantity = currentPagePickedQuantity + previousPagesPickedQuantity;

  const skippedQuantity = paginationData.isPaginated
    ? paginationData.totalItemsSkipped
    : calculateSkippedQuantity(pickTour);

  const totalQuantity = paginationData.isPaginated ? paginationData.totalItems : calculateTotalQuantity(pickTour);

  const unpickedQuantity = totalQuantity - (resolvedPickPickedQuantity + skippedQuantity);

  const hasOverstockItems = pickTour.picks.some(pick => pick.orderType === ORDER_TYPE_OVERSTOCK);

  const nonPickableQuantity = pickTour.picks
    .filter(pick => !pick.isEligibleForPicking)
    .reduce((total, pick) => total + pick.totalQuantity, 0);

  const lineItems = [
    {
      label: 'ITEMS_PICKED',
      quantity: resolvedPickPickedQuantity,
    },
    {
      label: 'ITEMS_RETURNED',
      quantity: unpickedQuantity,
    },
    {
      label: hasOverstockItems && paginationData.isPaginated ? 'ITEMS_NOT_FOUND' : 'ITEMS_SKIPPED',
      quantity: skippedQuantity,
    },
    ...(nonPickableQuantity > 0
      ? [
          {
            label: 'NON_PICKABLE_ITEMS',
            quantity: nonPickableQuantity,
          },
        ]
      : []),
  ];

  return (
    <section className={styles.section}>
      <article>
        <div className={styles.header}>
          <ReturnToQueueIcon />
          <h6 className={mergeClassNames(styles.paused, currentLanguage === LANGUAGE_SPANISH && styles.pausedSpanish)}>
            {t(`${translationRoot}.PAUSED`)}
          </h6>
        </div>
        {lineItems.map(line => (
          <div className={styles.lineItem} key={`${line.label}-${line.quantity}`}>
            <span>{t(`${translationRoot}.${line.label}`)}</span>
            <h6 className={styles.lineItemCount}>{line.quantity}</h6>
          </div>
        ))}
      </article>
      <footer>
        <Button data-cy="pick-tour-complete-button" onClick={returnToOrderList}>
          {t(`${translationRoot}.RETURN`)}
        </Button>
      </footer>
    </section>
  );
};

export default PickTourExitReview;
